<template>
	<div class="row inputs-containers">
		<div class="row input-container">
			<p>{{ getString('name') }}</p>
			<AppTextInput class="ml-auto" ref="name" :placeholder="getString('name')" :value="person ? person.name : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('email') }}</p>
			<AppTextInput class="ml-auto" ref="email" :placeholder="getString('email')" type="email" :value="person && person.email ? person.email : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('phone') }}</p>
			<AppTextInput class="ml-auto" ref="phone" :placeholder="getString('phone')" type="phone" :value="person ? person.phone : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('address') }}</p>
			<AppGoogleMapsInput class="text-input ml-auto" :placeholder="getString('address')" no-map @changed-address="changedAddress" :value="person ? person.address : null" />
		</div>
		<div class="row input-container">
			<p>{{ getString('fiscal_code') }}</p>
			<AppTextInput class="ml-auto" ref="fiscal" :placeholder="getString('fiscal_code')" :value="person ? person.fiscal_code : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('marketing_consent') }}</p>
			<AppSwitch class="ml-auto" ref="privacy" :checked="person ? person.privacy : false" @switched="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('birth_date') }}</p>
			<DatePicker ref="date-picker" v-model="date" mode="date" color="teal" :max-date='tenYearsAgo'  @update:modelValue="toUpdate = true" :is-dark="userStore.darkMode">
				<template #default="{ togglePopover }">
					<AppTextInput class="ml-auto text-input" @click="togglePopover()" readonly :placeholder="getString('birth_date')" :value="date ? formatDateYYMMDD(date) : null" />
				</template>
			</DatePicker>
		</div>
		<div class="row input-container">
			<p>{{ getString('gender') }}</p>
			<AppRadioButtonGroup ref="gender" allow-no-selection :values="[getString('male'), getString('female'), getString('other')]" :selected="getSelectedGender()" @radio-chosen="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('notes') }}</p>
			<AppTextInput class="ml-auto" ref="notes" textarea :placeholder="getString('notes')" :value="person ? person.notes : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('delete') }}</p>
			<AppButton class="right-item" small outline :text="getString('delete_customer')" @click="clickDeletePerson" />
		</div>
		<AppPopup
			:title="getString('confirm_delete_customer')"
			:body-text="getString('confirm_delete_customer_explain')"
			:buttons="popupDeleteButtons"
			ref="confirm-delete"
			prevent-overlay-dismiss
		/>
	</div>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useCounterStore, useCustomersStore, useUserStore} from "@/console-new/stores";
import {formatDateMMDDHHmm, formatDateTime, formatDateYYMMDD} from "@/shared/helpers/dates";
import {validateEmail, validatePhone} from "@/shared/helpers/strings";
import API from "@/shared/helpers/API";
import AppSwitch from "@/shared/components/AppSwitch.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		const counterStore = useCounterStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, customersStore, formatDateMMDDHHmm, formatDateYYMMDD, formatDateTime, counterStore}
	},
	name: "CustomersPersonEditDetails",
	emits: ['deleted-customer'],
	components: {AppSwitch, AppButton, AppPopup, AppRadioButtonGroup, AppGoogleMapsInput, AppTextInput},
	props: {
		personId: {
			type: Number,
			default: null,
		}
	},
	data() {
		return {
			tenYearsAgo: new Date(new Date().setFullYear(new Date().getFullYear() - 10)),
			date: this.person && this.person.birth_date ? this.person.birth_date : null,
			address: null,
			toUpdate: false,
			loadingDelete: false,
		}
	},
	computed: {
		person() {
			return this.personId ? this.customersStore.customersPeople.find(p => p.customer_id === this.personId) : null;
		},
		popupDeleteButtons() {
			return [
				{text: this.getString('cancel'), callback: null, negative: true, loading: this.loadingDelete},
				{text: this.getString('ok'), callback: this.deletePerson, loading: this.loadingDelete},
			]
		}
	},
	methods: {
		changedAddress(address) {
			this.address = address;
			this.toUpdate = true;
		},
		getSelectedGender() {
			if(!this.person) {
				return null;
			}
			if(this.person.gender === "male") {
				return 0;
			} else if(this.person.gender === "female") {
				return 1;
			} else if(this.person.gender === "other") {
				return 3;
			} else {
				return null;
			}
		},
		updatePerson() {
			if(this.toUpdate) {

				let name = this.$refs['name'].currentValue;
				if(!name || name.length <= 2) {
					this.showNotification("name-required");
					return;
				}

				let email = this.$refs['email'].currentValue;
				email = email && email.length > 0 ? email : null;
				if(email && !validateEmail(email)) {
					this.showNotification("wrong-email");
					return;
				}

				let phone = this.$refs['phone'].currentValue;
				phone = phone && phone.length > 0 ? phone : null;
				if(phone && !validatePhone(phone)) {
					this.showNotification("wrong-phone");
					return;
				}

				let fiscal = this.$refs['fiscal'].currentValue;
				fiscal = fiscal && fiscal.length > 0 ? fiscal : null;
				if(fiscal && fiscal.length < 11) {
					this.showNotification("wrong-fiscal");
					return;
				}

				let genderIndex = this.$refs['gender'].currentSelection;
				let gender = null;
				if(genderIndex === 0) {
					gender = 'male';
				} else if(genderIndex === 1) {
					gender = 'female';
				} else if(genderIndex === 2) {
					gender = 'other';
				}

				let notes = this.$refs['notes'].currentValue;
				notes = notes && notes.length > 0 ? notes : null;

				let date = this.date;
				let birthDate = date ? this.formatDateTime(date, false) : null;

				let address = this.address;

				let privacy = this.$refs['privacy'].isChecked;

				API.init().addCustomerPerson(this.userStore.currBusinessId, this.person.customer_id, name, email, phone, fiscal, gender, birthDate, address ? JSON.stringify(address) : null, notes, privacy, (response) => {
					if(response && response.result && response.result === "OK") {
						this.showNotification("generic-success");
						this.person.name = name;
						this.person.email = email;
						this.person.phone = phone;
						this.person.fiscal_code = fiscal;
						this.person.notes = notes;
						this.person.birth_date = date;
						this.person.gender = gender;
						this.person.privacy = privacy;
						if(address) {
							this.person.address = address.address;
							this.person.address_id = response.address_id;
						}
						if(this.counterStore.selectedCustomer && this.counterStore.selectedCustomer.customer_id === this.person.customer_id) {
							this.counterStore.selectedCustomer.name = name;
							this.counterStore.selectedCustomer.email = email;
							this.counterStore.selectedCustomer.phone = phone;
							this.counterStore.selectedCustomer.fiscal_code = fiscal;
							this.counterStore.selectedCustomer.notes = notes;
							this.counterStore.selectedCustomer.birth_date = date;
							this.counterStore.selectedCustomer.gender = gender;
							this.counterStore.selectedCustomer.privacy = privacy;
							if(address) {
								this.counterStore.selectedCustomer.address = address.address;
								this.counterStore.selectedCustomer.address_id = response.address_id;
							}
						}
					} else {
						this.showNotification("generic-fail");
					}
				});

			}
		},
		clickDeletePerson() {
			this.$refs['confirm-delete'].toggleStatus(true);
		},
		deletePerson() {
			this.loadingDelete = true;
			API.init().deleteCustomerPerson(this.userStore.currBusinessId, this.personId, resp=>{
				this.loadingDelete = false;
				if(resp && resp.result && resp.result === "OK") {
					this.showNotification("generic-success");
					this.$refs['confirm-delete'].toggleStatus(false);
					this.person.deleted = true;
					this.customersStore.customersPeople = this.customersStore.customersPeople.filter(p => p.customer_id !== this.personId);
					if(this.counterStore?.selectedCustomer) {
						this.counterStore.selectedCustomer = null;
					}
					this.$emit('deleted-customer', this.personId);
				} else if(resp && resp.details && resp.details === "not_completed_orders") {
					this.showNotification("delete-customer-open-orders-error");
				} else if(resp && resp.details && resp.details === "not_paid_later_orders") {
					this.showNotification("delete-customer-later-orders-error");
				} else if(resp && resp.details && resp.details === "credit_available") {
					this.showNotification("delete-customer-credit-error");
				} else if(resp && resp.details && resp.details === "debit_left") {
					this.showNotification("delete-customer-debit-error");
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
	},
	mounted() {
		this.date = this.person && this.person.birth_date ? this.person.birth_date : null;
	},
	watch: {
		personId(newValue, oldValue) {
			if(newValue !== oldValue) {
				this.date = this.person && this.person.birth_date ? this.person.birth_date : null;
				if(newValue) {
					this.toUpdate = false;
				}
			}
		},
	},
}
</script>

<style scoped lang="scss">
.inputs-containers {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.input-container {
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		justify-content: space-between;
		align-items: center;
		position: relative;
		padding: 8px 12px;

		.ml-auto {
			margin-left: auto;
		}

		&:after {
			content: "";
			position: absolute;
			top: 100%;
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-alpha-3);
		}

		&:last-child {
			&:after {
				display: none;
			}
		}
	}
}
</style>